import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Alert,
  IconButton,
  Container,
} from "@mui/material";
import {
  Add as AddIcon,
  ArrowBack as ArrowBackIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import usePublicGallery from "../hooks/usePublicGallery";
import GalleryManager from "../components/GalleryManager";
import GalleryList from "../components/GalleryList";
import GalleryForm from "../components/GalleryForm";
import GalleryImageUpload from "../components/GalleryImageUpload";
import Loader from "../../../components/common/Loader";
import Modal from "../../../components/common/Modal";
import { storage } from "../../../config/firebase";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";

const GalleryPage = () => {
  const {
    galleries,
    selectedGallery,
    loading,
    error,
    success,
    filters,
    getGalleries,
    getGalleryById,
    createNewGallery,
    updateExistingGallery,
    deleteExistingGallery,
    publishExistingGallery,
    unpublishExistingGallery,
    uploadNewImages,
    deleteExistingImage,
    toggleImageVisibility,
    setCoverImage,
    updateSettings,
    clearErrorState,
    clearSuccessState,
    updateFilters,
    clearSelectedGallery,
  } = usePublicGallery();

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedGalleryId, setSelectedGalleryId] = useState(null);
  const [viewMode, setViewMode] = useState("list");
  const [loadingAction, setLoadingAction] = useState(false);

  useEffect(() => {
    getGalleries(filters);
  }, [filters]);

  const handleSelectGallery = (id) => {
    setSelectedGalleryId(id);
    getGalleryById(id);
    setViewMode("details");
  };

  const handleBackToList = () => {
    setSelectedGalleryId(null);
    clearSelectedGallery();
    setViewMode("list");
  };

  const withLoading = async (fn) => {
    setLoadingAction(true);
    try {
      await fn();
      clearErrorState();
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingAction(false);
    }
  };

  const handleCreateGallery = (data) =>
    withLoading(async () => {
      await createNewGallery(data, { id: "USR001", name: "Admin" });
      setShowCreateModal(false);
      await getGalleries(filters);
    });

  const handleUpdateGallery = (data) =>
    withLoading(async () => {
      await updateExistingGallery(selectedGalleryId, data);
      setShowEditModal(false);
      await getGalleryById(selectedGalleryId);
    });

  const handleDeleteGallery = (id) => {
    if (window.confirm("Are you sure you want to delete this gallery?")) {
      withLoading(async () => {
        await deleteExistingGallery(id);
        if (selectedGalleryId === id) handleBackToList();
        await getGalleries(filters);
      });
    }
  };

  const handlePublish = (id) =>
    withLoading(async () => {
      await publishExistingGallery(id);
      await getGalleries(filters);
    });
  const handleUnpublish = (id) =>
    withLoading(async () => {
      await unpublishExistingGallery(id);
      await getGalleries(filters);
    });

  const handleUploadImages = (files) =>
    withLoading(async () => {
      if (!selectedGalleryId) return;
      const uploadPromises = files.map(async (file) => {
        const timestamp = Date.now();
        const uniqueId = Math.random().toString(36).substring(2, 8);
        const fileName = `${timestamp}_${uniqueId}_${file.name}`;
        const storagePath = `publicGallery/${selectedGalleryId}/images/${fileName}`;
        const storageRef = ref(storage, storagePath);
        await uploadBytes(storageRef, file);
        const url = await getDownloadURL(storageRef);
        return { url, thumbnailUrl: url };
      });
      const imagesData = await Promise.all(uploadPromises);
      await uploadNewImages(selectedGalleryId, imagesData);
      await getGalleryById(selectedGalleryId);
      setShowUploadModal(false);
    });

  const handleDeleteImage = (imageUrl) =>
    withLoading(async () => {
      await deleteExistingImage(selectedGalleryId, imageUrl);
      await getGalleryById(selectedGalleryId);
    });

  const handleToggleVisibility = (imageUrl) =>
    withLoading(async () => {
      await toggleImageVisibility(selectedGalleryId, imageUrl);
      await getGalleryById(selectedGalleryId);
    });

  const handleSetCover = (imageUrl, thumbnailUrl) =>
    withLoading(async () => {
      await setCoverImage(selectedGalleryId, imageUrl, thumbnailUrl);
      await getGalleryById(selectedGalleryId);
    });

  const handleSettingsUpdate = (settingsUpdates) =>
    withLoading(async () => {
      await updateSettings(selectedGalleryId, settingsUpdates);
      await getGalleryById(selectedGalleryId);
    });

  if (loading && !galleries.length) return <Loader fullScreen />;

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 4,
        }}
      >
        <Box>
          <Typography variant="h4" component="h1" fontWeight="bold">
            Public Galleries
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage public-facing photo galleries
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="primary"
          onClick={() => setShowCreateModal(true)}
          startIcon={<AddIcon />}
          disabled={loadingAction}
        >
          New Gallery
        </Button>
      </Box>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 3 }}
          action={
            <IconButton size="small" onClick={clearErrorState}>
              <CloseIcon fontSize="inherit" />
            </IconButton>
          }
        >
          {error}
        </Alert>
      )}
      {success && (
        <Alert
          severity="success"
          sx={{ mb: 3 }}
          action={
            <IconButton size="small" onClick={clearSuccessState}>
              <CloseIcon fontSize="inherit" />
            </IconButton>
          }
        >
          {success}
        </Alert>
      )}

      {viewMode === "list" && (
        <GalleryList
          galleries={galleries}
          onSelect={handleSelectGallery}
          onDelete={handleDeleteGallery}
          onPublish={handlePublish}
          onUnpublish={handleUnpublish}
          filters={filters}
          onFilterChange={updateFilters}
          loading={loading}
        />
      )}

      {viewMode === "details" && selectedGallery && (
        <Box>
          <Button
            onClick={handleBackToList}
            startIcon={<ArrowBackIcon />}
            sx={{ mb: 3 }}
          >
            Back to Galleries
          </Button>
          <GalleryManager
            gallery={selectedGallery}
            onEdit={() => setShowEditModal(true)}
            onDelete={() => handleDeleteGallery(selectedGallery.id)}
            onUpload={() => setShowUploadModal(true)}
            onDeleteImage={handleDeleteImage}
            onToggleVisibility={handleToggleVisibility}
            onSetCover={handleSetCover}
            onSettingsUpdate={handleSettingsUpdate}
          />
        </Box>
      )}

      <Modal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Create New Gallery"
      >
        <GalleryForm
          onSubmit={handleCreateGallery}
          onCancel={() => setShowCreateModal(false)}
          loading={loadingAction}
        />
      </Modal>

      <Modal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        title="Edit Gallery"
      >
        <GalleryForm
          initialData={selectedGallery}
          onSubmit={handleUpdateGallery}
          onCancel={() => setShowEditModal(false)}
          loading={loadingAction}
        />
      </Modal>

      <Modal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        title="Upload Images"
        size="lg"
      >
        <GalleryImageUpload
          onUpload={handleUploadImages}
          onCancel={() => setShowUploadModal(false)}
          loading={loadingAction}
        />
      </Modal>
    </Container>
  );
};

export default GalleryPage;
